import { cloneDeep } from "lodash";
import moment from "moment";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { ksnFilterOptions } from "../ksn";
import { KsnFilterSettings } from "../settings/KsnFilterSettings";
import { KsnFilterSettingsForm } from "../settings-form/KsnFilterSettingsForm";
import { KsnTableDataRequest } from "../data-request/KsnTableDataRequest";
import { KsnJournalRequest } from "../journal/KsnJournalRequest";
import { KsnTableData } from "../table-data/KsnTableData";

export class KsnFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(ksnFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        if (settings.reportName !== 'KSN') throw new Error('error_type_filter_settings')
        if (options.key !== 'KSN') throw new Error('error_type_filter_options')

        const s = settings as KsnFilterSettings
        const o = options as typeof ksnFilterOptions

        const reportName = cloneDeep(o.reportNames.find(it => it.value === 'KSN')!)
        const reportType = cloneDeep(o.reportTypes.find(it => it.value === s.reportType)!)
        const classificationType = cloneDeep(o.classificationTypes.find(it => it.value === s.classificationType)!)
        const dataSource = cloneDeep(o.dataSources.find(it => it.value === s.dataSource)!)
        const periodicity = cloneDeep(o.periodicities.find(it => it.value === s.periodicity)!)
        const regionOption = cloneDeep(o.regions.find(it => it.value === s.region) ?? null)
        const budgetTypeOptions = cloneDeep(o.budgetTypes.filter(it => s.budgetTypes.includes(it.value)))
        const measureUnitOption = cloneDeep(o.measureUnits.find(it => it.value === s.measureUnit) ?? null)

        const form: KsnFilterSettingsForm = {
            key: 'KSN',
            reportName,
            reportType,
            classificationType,
            dataSource,
            periodicity,
            bdate: s.bdate ? new Date(s.bdate) : null,
            edate: s.edate ? new Date(s.edate) : null,
            region: regionOption,
            budgetTypes: budgetTypeOptions,
            measureUnit: measureUnitOption,
            roundUp: s.roundUp ?? false,
            roundUpTo: s.roundUpTo,
            sortByRegionCode: s.sortByRegionCode ?? false,
        }
        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'KSN') throw new Error('error_type_filter_settingsForm')
        const f = form as KsnFilterSettingsForm

        const settings: KsnFilterSettings = {
            reportName: 'KSN',
            reportType: f.reportType.value,
            classificationType: f.classificationType.value,
            dataSource: f.dataSource.value,
            periodicity: f.periodicity.value,
            bdate: f.bdate ? moment(f.bdate).format('YYYY-MM-DD') : null,
            edate: f.edate ? moment(f.edate).format('YYYY-MM-DD') : null,
            region: f.region?.value ?? null,
            budgetTypes: f.budgetTypes.map(it => it.value),
            measureUnit: f.measureUnit?.value ?? null,
            roundUp: f.roundUp,
            roundUpTo: f.roundUpTo,
            sortByRegionCode: f.sortByRegionCode ?? false,
        }
        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(ksnFilterOptions)
        const now = new Date()
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)

        const form: KsnFilterSettingsForm = {
            key: 'KSN',
            reportName: options.reportNames.find(it => it.value === 'KSN')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'PERIOD')!,
            bdate: firstDay,
            edate: lastDay,
            region: null,
            budgetTypes: options.budgetTypes.filter(it => [2, 3, 6].includes(it.value)),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND') ?? null,
            roundUp: false,
            roundUpTo: null,
            sortByRegionCode: false,
        }
        return form
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        if (form.key !== 'KSN') throw new Error('error_type_filter_settingsForm')
        const f = form as KsnFilterSettingsForm

        if (!f.bdate || !f.edate) throw new Error('error_input_period')
        if (f.bdate > f.edate) throw new Error('error_date_from_greater_than_to')

        const bdate = moment(f.bdate).format('YYYY-MM-DD')
        const edate = moment(f.edate).format('YYYY-MM-DD')

        const params: KsnTableDataRequest = {
            bdate,
            edate,
            region: f.region?.value ?? null,
            budget_type: f.budgetTypes.map(it => it.value),
            measure_unit: f.measureUnit?.value ?? 'THOUSAND',
            sort_by_region_code: f.sortByRegionCode ?? false,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/KSN', {
            method: 'POST',
            body: JSON.stringify(params),
        })
        if (!response.ok) throw new Error('error_api_report_data_failed')

        const responseData: KsnTableData = await response.json()

        const settings = this.formToSettings(cloneDeep(form))
        const journalParams: KsnJournalRequest = {
            reportName: 'KSN',
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            bdate,
            edate,
            measure_unit: f.measureUnit?.value ?? 'THOUSAND',
            round_up: f.roundUp,
            round_up_to: f.roundUp ? f.roundUpTo : null,
            months: responseData.months,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(responseData),
            journalParams,
        }
        return result
    }
}
